import {
  type Dispatch,
  type FC,
  type SetStateAction,
  useEffect,
  useMemo,
} from 'react';
import { useParams } from 'react-router-dom';
import { type RectReadOnly } from 'react-use-measure';
import RefreshIcon from '@assets/img/icons/refresh.svg?react';
import { AppRoute } from '@common/app/routes';
import { getImage } from '@common/lib/images';
import { type PlantTypeText } from '@common/types/plant';
import { type ResourceTypeText } from '@common/types/resource';
import { H2 } from '@components/Typography/Typography';
import { Button, LinkButton } from '@components/ui/Button/Button';
import { Picture } from '@components/ui/Picture/Picture';
import { useModal } from '@context/ModalContext';
import { useAppSelector } from '@redux/store';
import {
  AnimatePresence,
  cubicBezier,
  motion,
  useAnimation,
} from 'framer-motion';

import { multiplication } from '@/common/lib/decimal';
import { ClaimCashCardAnimation } from '@/components/Animations/ClaimItemAnimation/ClaimCashCardAnimation/ClaimCashCardAnimation';
import { StorageIndicator } from '@/components/ui/StorageIndicator/StorageIndicator';

import {
  generageTimes,
  materialImgAnimation,
  refreshIconAnimation,
  resourceImgAnimation,
} from './config';

import css from './styles.module.scss';

interface ProductionAnimationProps {
  material: PlantTypeText;
  resource: ResourceTypeText;
  isInProgress: boolean;
  pullAnimating: boolean;
  setPullAnimating: Dispatch<SetStateAction<boolean>>;
  pullCoords?: RectReadOnly | null;
  toAdd?: number;
  productionRate: number;
}

const H2Motion = motion.create(H2);
const MotionPicture = motion.create(Picture);
const ProductionAnimation: FC<ProductionAnimationProps> = ({
  material,
  resource,
  isInProgress,
  pullAnimating,
  setPullAnimating,
  pullCoords,
  toAdd,
  productionRate,
}) => {
  const { id } = useParams();
  const { sellModal } = useModal();
  const storage = useAppSelector((state) => state.user.user?.storage);
  const refreshAnimationControll = useAnimation();
  const resourceAnimationControll = useAnimation();
  const materialAnimationControll = useAnimation();
  const refreshAnimationTimes = useMemo(
    () => generageTimes(2.6, refreshIconAnimation.timings),
    [],
  );
  const resourceAnimationTimes = useMemo(
    () => generageTimes(0.7, resourceImgAnimation.timings),
    [],
  );
  const materialAnimationTimes = useMemo(
    () => generageTimes(1, materialImgAnimation.timings),
    [],
  );

  useEffect(() => {
    console.log('isInProgress', isInProgress);

    if (isInProgress) {
      refreshAnimationControll.start({
        rotate: refreshIconAnimation.rotate,
        scale: refreshIconAnimation.scale,
        transition: {
          ease: cubicBezier(0.175, 0.885, 0.32, 1.275),
          times: refreshAnimationTimes,
          duration: 2.6,
          delay: 0.8,
          repeatDelay: 0.3,
          repeat: Infinity,
        },
      });
      resourceAnimationControll.start({
        opacity: resourceImgAnimation.opacity,
        left: resourceImgAnimation.left,
        transition: {
          ease: cubicBezier(0.175, 0.885, 0.32, 1.275),
          times: resourceAnimationTimes,
          delay: 0.8,
          duration: 0.7,
          repeat: Infinity,
          repeatDelay: 2.2,
        },
      });
      materialAnimationControll.start({
        opacity: materialImgAnimation.opacity,
        left: materialImgAnimation.left,
        scale: materialImgAnimation.scale,
        filter: materialImgAnimation.filter,
        x: materialImgAnimation.x,
        transition: {
          ease: cubicBezier(0.175, 0.885, 0.32, 1.275),
          times: materialAnimationTimes,
          delay: 2.4,
          duration: 1,
          repeat: Infinity,
          repeatDelay: 1.9,
        },
      });
    } else {
      refreshAnimationControll.stop();
      resourceAnimationControll.stop();
      materialAnimationControll.stop();
    }
  }, [isInProgress, id]);

  const openSellModal = (): void => {
    const item = storage && storage[material];

    sellModal({
      key: material,
      value: { value: item?.value || 0, costPerUnit: item?.costPerUnit || 0 },
    });
  };

  const materialIcon = getImage({ name: material, field: 'icon' });
  const resourceIcon = getImage({ name: resource, field: 'icon' });

  const resourcesForProductionCount = multiplication(
    toAdd || 0,
    productionRate,
  );

  return (
    <div className="w-full px-2 flex items-center gap-2">
      <div className="w-[calc(100%-6rem)] h-8 mx-12 flex z-[2] absolute left-0 top-1/2 -translate-y-1/2">
        <div className="relative flex-1">
          <MotionPicture
            key={`resource-${id}`}
            className="absolute top-0 block w-8 h-8 opacity-0 -translate-x-1/2"
            animate={resourceAnimationControll}
            img={resourceIcon?.img}
            webp={resourceIcon?.webp}
            alt={resource}
          />
        </div>
        <div className="flex-1 relative">
          <MotionPicture
            key={`material-${id}`}
            className="absolute top-0 block w-8 h-8 opacity-0 -translate-x-1/2"
            animate={materialAnimationControll}
            img={materialIcon?.img}
            webp={materialIcon?.webp}
            alt={material}
          />
        </div>
      </div>
      <div className="relative z-[2]">
        <AnimatePresence>
          {resourcesForProductionCount && (
            <H2Motion
              initial={{ opacity: 0, scale: 0.6 }}
              animate={{ opacity: 1, scale: 1, y: -28 }}
              exit={{
                opacity: 0,
                scale: 0.3,
                y: -28,
                transition: { delay: 2 },
              }}
              weight="bold"
              className={css.value}
            >
              {resourcesForProductionCount * -1}
            </H2Motion>
          )}
        </AnimatePresence>
        <LinkButton
          className={'!w-auto relative z-[2]'}
          variant="transparent"
          to={AppRoute.RESOURCES + '/mineResource/' + resource}
        >
          <Picture
            className="block w-20 h-20"
            img={resourceIcon?.img}
            webp={resourceIcon?.webp}
            alt={resource}
          />
          {pullAnimating && pullCoords && (
            <ClaimCashCardAnimation
              icon={{
                img: resourceIcon?.img,
                webp: resourceIcon?.webp,
              }}
              reward={resource}
              finalPositon={pullCoords}
              scale={0.1}
              type="arc"
              onAnimationEnd={() => setPullAnimating(false)}
              delay={0.3}
            />
          )}
          <StorageIndicator
            iconClassName="!text-white/40 shrink-0"
            textClassName="!text-white/60"
            className="!bg-black/20 backdrop-blur-3xl w-max pr-1 absolute bottom-0 left-1/2 -translate-x-1/2 z-[3]"
            resource={resource}
          />
        </LinkButton>
      </div>
      <div className="flex-1 items-center flex justify-center">
        <span className="h-[1px] bg-white/40 min-w-8 w-[40%]"></span>
      </div>
      <motion.div
        key={`refresh-${id}`}
        animate={refreshAnimationControll}
        className="px-4 shrink-0 relative"
      >
        <RefreshIcon className="w-10 h-10" />
      </motion.div>
      <div className="flex-1 items-center flex justify-center">
        <span className="h-[1px] bg-white/40 min-w-8 w-[40%]"></span>
      </div>

      <div className="relative z-[2]">
        <AnimatePresence>
          {toAdd && (
            <H2Motion
              initial={{ opacity: 0, scale: 0.6 }}
              animate={{ opacity: 1, scale: 1, y: -28 }}
              exit={{
                opacity: 0,
                scale: 0.3,
                y: -28,
                transition: { delay: 2 },
              }}
              weight="bold"
              className={css.valueGreen}
            >
              {toAdd}
            </H2Motion>
          )}
        </AnimatePresence>
        <Button
          data-fancybox
          data-src="#sell-modal"
          onClick={openSellModal}
          variant="transparent"
          className="!w-auto relative"
        >
          <Picture
            className="block w-20 h-20"
            img={materialIcon?.img}
            webp={materialIcon?.webp}
            alt={material}
          />
          <StorageIndicator
            iconClassName="!text-white/40"
            textClassName="!text-white/60"
            className="!bg-black/20 backdrop-blur-3xl pr-1 w-max absolute bottom-0 left-1/2 -translate-x-1/2"
            resource={material}
          />
        </Button>
      </div>
    </div>
  );
};

export { ProductionAnimation };
